// Live data for the Messages page.
// RLS on message_threads / messages already scopes to participants.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const listMyThreads = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: threads, error } = await supabase
      .from("message_threads")
      .select("id, participant_a, participant_b, transaction_id, buy_order_id, last_message_at, last_read_a, last_read_b, created_at")
      .order("last_message_at", { ascending: false, nullsFirst: false })
      .limit(100);
    if (error) throw new Error(error.message);

    const ids = (threads ?? []).map((t) => t.id);
    if (ids.length === 0) return { threads: [] as Array<typeof enriched[number]> };

    const { data: lastMsgs } = await supabase
      .from("messages")
      .select("thread_id, body, filtered_body, blocked, created_at, sender_id")
      .in("thread_id", ids)
      .order("created_at", { ascending: false })
      .limit(500);
    const latestByThread = new Map<string, NonNullable<typeof lastMsgs>[number]>();
    const unreadByThread = new Map<string, number>();
    for (const m of lastMsgs ?? []) {
      if (!latestByThread.has(m.thread_id)) latestByThread.set(m.thread_id, m);
    }

    const counterIds = Array.from(
      new Set((threads ?? []).map((t) => (t.participant_a === userId ? t.participant_b : t.participant_a))),
    );
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .in("id", counterIds);
    const profileById = new Map((profiles ?? []).map((p) => [p.id, p]));

    // Resolve buy_order per thread (direct or via transaction).
    const txIds = Array.from(
      new Set(
        (threads ?? [])
          .filter((t) => !t.buy_order_id && t.transaction_id)
          .map((t) => t.transaction_id as string),
      ),
    );
    const txBuyOrder = new Map<string, string>();
    if (txIds.length > 0) {
      const { data: txs } = await supabase
        .from("transactions")
        .select("id, buy_order_id")
        .in("id", txIds);
      for (const t of txs ?? []) if (t.buy_order_id) txBuyOrder.set(t.id, t.buy_order_id);
    }
    const buyOrderIdByThread = new Map<string, string>();
    for (const t of threads ?? []) {
      const bo = t.buy_order_id ?? (t.transaction_id ? txBuyOrder.get(t.transaction_id) ?? null : null);
      if (bo) buyOrderIdByThread.set(t.id, bo);
    }
    const boIds = Array.from(new Set(buyOrderIdByThread.values()));
    const boById = new Map<string, { id: string; title: string; reference_image_path: string | null }>();
    if (boIds.length > 0) {
      const { data: bos } = await supabase
        .from("buy_orders")
        .select("id, title, card_name, reference_image_path")
        .in("id", boIds);
      for (const bo of bos ?? []) {
        boById.set(bo.id, {
          id: bo.id,
          title: bo.title || bo.card_name || "Buy order",
          reference_image_path: bo.reference_image_path ?? null,
        });
      }
    }
    const imgPaths = Array.from(boById.values())
      .map((b) => b.reference_image_path)
      .filter((p): p is string => !!p);
    const urlByPath = new Map<string, string>();
    if (imgPaths.length > 0) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: signed } = await supabaseAdmin.storage
          .from("buy-order-images")
          .createSignedUrls(imgPaths, 60 * 60);
        for (const s of signed ?? []) {
          if (s.path && s.signedUrl) urlByPath.set(s.path, s.signedUrl);
        }
      } catch (e) {
        console.error("[threads] sign buy-order images failed", e);
      }
    }

    // compute unread for each thread
    for (const t of threads ?? []) {
      const isA = t.participant_a === userId;
      const lastRead = isA ? t.last_read_a : t.last_read_b;
      const count = (lastMsgs ?? []).filter(
        (m) => m.thread_id === t.id && m.sender_id !== userId && (!lastRead || m.created_at > lastRead),
      ).length;
      unreadByThread.set(t.id, count);
    }

    const enriched = (threads ?? []).map((t) => {
      const counterId = t.participant_a === userId ? t.participant_b : t.participant_a;
      const p = profileById.get(counterId);
      const last = latestByThread.get(t.id);
      const boId = buyOrderIdByThread.get(t.id);
      const bo = boId ? boById.get(boId) : undefined;
      const order = bo
        ? {
            id: bo.id,
            title: bo.title,
            image_url: bo.reference_image_path ? urlByPath.get(bo.reference_image_path) ?? null : null,
            href: `/orders/${bo.id}`,
          }
        : t.transaction_id
          ? { id: t.transaction_id, title: "Order", image_url: null as string | null, href: `/_authenticated/transaction/${t.transaction_id}` }
          : null;
      return {
        id: t.id,
        transaction_id: t.transaction_id,
        last_message_at: t.last_message_at,
        counterparty_id: counterId,
        counterparty_username: p?.username ?? counterId.slice(0, 8),
        counterparty_display_name: p?.display_name ?? null,
        counterparty_avatar_url: p?.avatar_url ?? null,
        last_message: last ? (last.blocked ? last.filtered_body : last.body) : null,
        last_message_blocked: last?.blocked ?? false,
        unread_count: unreadByThread.get(t.id) ?? 0,
        order,
      };
    });

    return { threads: enriched };
  });

export type ThreadMessageAttachment = { path: string; url: string | null; content_type?: string };
export type ThreadMessage = {
  id: string;
  thread_id: string;
  sender_id: string;
  body: string;
  filtered_body: string | null;
  blocked: boolean;
  created_at: string;
  attachments: ThreadMessageAttachment[];
};
export type ThreadOrderLink = {
  kind: "buy_order" | "transaction";
  id: string;
  title: string;
  href: string;
  image_url: string | null;
};

export const listThreadMessages = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ thread_id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }): Promise<{ messages: ThreadMessage[]; orderLink: ThreadOrderLink | null }> => {
    const { supabase } = context;
    const [{ data: messages, error }, { data: thread }] = await Promise.all([
      supabase
        .from("messages")
        .select("id, thread_id, sender_id, body, filtered_body, blocked, flags, created_at, attachments" as never)
        .eq("thread_id", data.thread_id)
        .order("created_at", { ascending: true })
        .limit(500),
      supabase
        .from("message_threads")
        .select("id, transaction_id, buy_order_id")
        .eq("id", data.thread_id)
        .maybeSingle(),
    ]);
    if (error) throw new Error(error.message);
    const rows = (messages ?? []) as unknown as Array<ThreadMessage & { attachments: unknown }>;

    // Resolve order link (prefer buy_order; fall back to transaction's buy_order; else transaction).
    let orderLink: ThreadOrderLink | null = null;
    if (thread) {
      let buyOrderId: string | null = thread.buy_order_id ?? null;
      if (!buyOrderId && thread.transaction_id) {
        const { data: t } = await supabase
          .from("transactions")
          .select("id, buy_order_id")
          .eq("id", thread.transaction_id)
          .maybeSingle();
        buyOrderId = t?.buy_order_id ?? null;
        if (!buyOrderId && t) {
          orderLink = {
            kind: "transaction",
            id: t.id,
            title: "Order",
            href: `/_authenticated/transaction/${t.id}`,
            image_url: null,
          };
        }
      }
      if (buyOrderId) {
        const { data: bo } = await supabase
          .from("buy_orders")
          .select("id, title, card_name, reference_image_path")
          .eq("id", buyOrderId)
          .maybeSingle();
        if (bo) {
          let image_url: string | null = null;
          if (bo.reference_image_path) {
            try {
              const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
              const { data: signed } = await supabaseAdmin.storage
                .from("buy-order-images")
                .createSignedUrl(bo.reference_image_path, 60 * 60);
              image_url = signed?.signedUrl ?? null;
            } catch (e) {
              console.error("[thread] sign image failed", e);
            }
          }
          orderLink = {
            kind: "buy_order",
            id: bo.id,
            title: bo.title || bo.card_name || "Buy order",
            href: `/orders/${bo.id}`,
            image_url,
          };
        }
      }
    }


    // Collect all attachment paths and mint signed URLs via admin client (bucket is private).
    const paths: string[] = [];
    for (const m of rows) {
      const list = Array.isArray(m.attachments) ? (m.attachments as Array<{ path?: string }>) : [];
      for (const a of list) if (a?.path) paths.push(a.path);
    }
    const urlByPath = new Map<string, string>();
    if (paths.length > 0) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: signed } = await supabaseAdmin.storage
        .from("message-attachments")
        .createSignedUrls(paths, 60 * 60);
      for (const s of signed ?? []) {
        if (s.path && s.signedUrl) urlByPath.set(s.path, s.signedUrl);
      }
    }
    const enriched: ThreadMessage[] = rows.map((m) => {
      const list = Array.isArray(m.attachments)
        ? (m.attachments as Array<{ path: string; content_type?: string }>)
        : [];
      return {
        ...m,
        attachments: list.map((a) => ({ path: a.path, url: urlByPath.get(a.path) ?? null, content_type: a.content_type })),
      };
    });
    return { messages: enriched, orderLink };
  });


export const markThreadRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ thread_id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: thread } = await supabase
      .from("message_threads")
      .select("participant_a, participant_b")
      .eq("id", data.thread_id)
      .maybeSingle();
    if (!thread) throw new Error("Thread not found");
    const isA = thread.participant_a === userId;
    const isB = thread.participant_b === userId;
    if (!isA && !isB) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const patch = isA ? { last_read_a: new Date().toISOString() } : { last_read_b: new Date().toISOString() };
    await supabaseAdmin.from("message_threads").update(patch).eq("id", data.thread_id);
    // Also clear "new message" notifications pointing to this thread for the viewer.
    await supabaseAdmin
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("type", "message")
      .eq("link", `/messages/${data.thread_id}`)
      .is("read_at", null);
    return { ok: true };
  });

export const getUnreadCounts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const [{ count: notifUnread }, { data: threads }] = await Promise.all([
      supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .is("read_at", null),
      supabase
        .from("message_threads")
        .select("id, participant_a, participant_b, last_read_a, last_read_b")
        .limit(100),
    ]);

    let msgUnread = 0;
    for (const t of threads ?? []) {
      const isA = t.participant_a === userId;
      const lastRead = isA ? t.last_read_a : t.last_read_b;
      const q = supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("thread_id", t.id)
        .neq("sender_id", userId);
      const { count } = lastRead ? await q.gt("created_at", lastRead) : await q;
      msgUnread += count ?? 0;
    }

    return { notifications: notifUnread ?? 0, messages: msgUnread };
  });
